import type { Control, FieldErrors, UseFormRegister } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { Card } from '@/components/ui/Card';
import { CheckboxField } from '@/components/forms/CheckboxField';
import { Checkbox } from '@/components/ui/Checkbox';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { UNDERREPRESENTED_CATEGORY_OPTIONS } from '@/lib/constants/underrepresented-categories';
import type { InvestorProfileFormValues } from './schema';
import { INVESTOR_PREF_LEVEL_OPTIONS, INVESTOR_PREF_LEVEL_TONE_BY_VALUE } from './investor-options';

const S = UI_STRINGS.INVESTOR_PROFILE;

function LevelControl({
  control,
  name,
  label,
  error,
}: {
  control: Control<InvestorProfileFormValues>;
  name: 'prefFemaleLed' | 'prefUnderrepresented';
  label: string;
  error?: string;
}) {
  return (
    <>
      {/* Stacked, not side by side: these labels ("Only invests in", "No preference")
          are far longer than the Market Model ones, so label + control on one row
          overflows the edit sidebar. The control fills the row in equal segments. */}
      <div className="flex flex-col gap-2">
        <span className="text-p6 text-navy font-semibold">{label}</span>
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <SegmentedControl
              label={label}
              options={INVESTOR_PREF_LEVEL_OPTIONS}
              value={field.value ?? undefined}
              onChange={field.onChange}
              toneByValue={INVESTOR_PREF_LEVEL_TONE_BY_VALUE}
              size="sm"
              divided
            />
          )}
        />
      </div>
      {error && (
        <p role="alert" className="text-danger text-xs">
          {error}
        </p>
      )}
    </>
  );
}

function FounderPreferencesEditor({
  control,
  register,
  errors,
}: {
  control: Control<InvestorProfileFormValues>;
  register: UseFormRegister<InvestorProfileFormValues>;
  errors?: FieldErrors<InvestorProfileFormValues>;
}) {
  // Categories only make sense once "Underrepresented founders" is anything
  // other than "no preference". `useWatch` needs a mounted form, so it lives
  // in this edit-only child.
  const prefUnderrepresented = useWatch({ control, name: 'prefUnderrepresented' });
  return (
    <div className="flex flex-col gap-4">
      <LevelControl
        control={control}
        name="prefFemaleLed"
        label={S.FEMALE_LED_STARTUPS}
        error={errors?.prefFemaleLed?.message}
      />
      <LevelControl
        control={control}
        name="prefUnderrepresented"
        label={S.UNDERREPRESENTED_FOUNDERS}
        error={errors?.prefUnderrepresented?.message}
      />
      {prefUnderrepresented && prefUnderrepresented !== 'no_preference' && (
        <fieldset className="flex flex-col gap-2">
          <FieldLabel label={S.UNDERREPRESENTED_CATEGORIES} variant="caps" />
          <p className="text-p6 text-slate italic">{S.UNDERREPRESENTED_CATEGORIES_HINT}</p>
          <div className="flex flex-wrap gap-4">
            {UNDERREPRESENTED_CATEGORY_OPTIONS.map((option) => (
              <Checkbox
                key={option.value}
                value={option.value}
                label={option.label}
                {...register('prefUnderrepresentedCategories')}
              />
            ))}
          </div>
        </fieldset>
      )}
      <CheckboxField
        {...register('interestOfficeHours')}
        variant="toggle"
        label={S.OFFICE_HOURS_INTEREST}
      />
      <CheckboxField
        {...register('interestFounderFunderEvents')}
        variant="toggle"
        label={S.FOUNDER_FUNDER_EVENTS_INTEREST}
      />
    </div>
  );
}

interface FounderPreferencesSectionProps {
  control: Control<InvestorProfileFormValues>;
  register: UseFormRegister<InvestorProfileFormValues>;
  errors?: FieldErrors<InvestorProfileFormValues>;
}

/**
 * The Founder preferences panel's fields (FR-175): female-led and
 * underrepresented-founder preferences plus the office-hours and events
 * interests. The read view is `FounderPreferencesCard` (MYN-217).
 */
export function FounderPreferencesSection({
  control,
  register,
  errors,
}: FounderPreferencesSectionProps) {
  return (
    <Card title={S.FOUNDER_PREFERENCES} headerDivider hoverEffect={false}>
      <FounderPreferencesEditor control={control} register={register} errors={errors} />
    </Card>
  );
}
